import React, { useMemo } from "react";
import "./ContainerStatus.scss";
import { JobBreakUpDetail } from "../../interfaces/job.interface";

interface ContainerStatusProps {
  container?: JobBreakUpDetail;
  status?: string;
  highlightSearchMatches?: boolean;
  searchQuery?: string;
  title?: string;
}

const ContainerStatus: React.FC<ContainerStatusProps> = ({
  container,
  status,
  highlightSearchMatches,
  searchQuery,
  title,
}) => {
  const styles = useMemo(() => {
    const baseStyles = {
      backgroundColor: "#f5f5f5",
      color: "#666666",
      borderColor: "#e0e0e0",
    };

    // First check if it's Available
    if (
      container?.tripStatus?.toUpperCase() === "INITIATED" &&
      container.containerNumber &&
      container.containerNumber !== "N/A"
    ) {
      return {
        backgroundColor: "#e3f2fd",
        color: "#1976d2",
        borderColor: "#90caf9",
      };
    }

    const currentStatus = container?.tripStatus || status;

    switch (currentStatus?.toUpperCase()) {
      case "DROPPED":
        return {
          backgroundColor: "#e8f5e9",
          color: "#2e7d32",
          borderColor: "#a5d6a7",
        };
      case "INITIATED":
        return {
          backgroundColor: "#f3e5f5",
          color: "#7b1fa2",
          borderColor: "#ce93d8",
        };
      case "PICKED":
      case "IN_TRANSIT":
        return {
          backgroundColor: "#fff3e0",
          color: "#f57c00",
          borderColor: "#ffcc80",
        };
      case "CANCELLED":
        return {
          backgroundColor: "#ffebee",
          color: "#d32f2f",
          borderColor: "#ef9a9a",
        };
      default:
        return baseStyles;
    }
  }, [container?.tripStatus, container?.containerNumber, status]);

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const displayText = useMemo(() => {
    if (!container) {
      return status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : "Unknown status";
    }

    if (container.tripStatus?.toUpperCase() === "INITIATED") {
      if (container.containerNumber && container.containerNumber !== "N/A") {
        return "Available";
      }
      return "Pending";
    }

    if (container.tripStatus?.toUpperCase() === "IN_TRANSIT") {
      return "Picked";
    }

    return container.tripStatus
      ? container.tripStatus.charAt(0).toUpperCase() +
          container.tripStatus.slice(1).toLowerCase()
      : "Unknown status";
  }, [container, status]);

  return (
    <div className="container-status" style={styles} title={title}>
      {highlightSearchMatches && searchQuery
        ? highlightText(displayText, searchQuery)
        : displayText}
    </div>
  );
};

export default ContainerStatus;
